import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReportMachine, ServiceType, ReportTimeLogInput, ReportStatus, ReportMachineRequest, ReportTimeLogRequest, Machine } from '../types';
import { apiClient } from '../services/apiClient';
import { useNotification } from '../hooks/useNotification';
import { useConfirm } from '../hooks/useConfirm';
import { useReport } from '../hooks/useReport';
import { useMachine } from '../hooks/useMachine';
import { useForeignAgentReadonly } from '../hooks/useForeignAgentReadonly';
import { format, differenceInMinutes } from 'date-fns';
import { safeFormatDate } from '../utils/dateUtils';
import { minutesToTimeString, timeStringToMinutes } from '../utils/timeUtils';
import { getDashboardPath } from '../utils/navigationUtils';
import MachineSelectionModal from '../components/forms/MachineSelectionModal';
import ServicePreparationSection from '../components/forms/ServicePreparationSection';
import MachineInfoSection from '../components/forms/MachineInfoSection';
import { ReportInfo, ReportStatusInfo } from '../components/sidebar';
import { FormHeader } from '../components/FormHeader';
import { BreadcrumbItem } from '../components/Breadcrumb';
import InfoTooltip from '../components/InfoTooltip/InfoTooltip';
import AiAssistantModal from '../components/AiAssistant/AiAssistantModal';
import '../components/AiAssistant/AiAssistantModal.css';
import { authService } from '../services/authService';
import './MachineForm.css';

const serializeMachineForDirtyCheck = (m: Partial<ReportMachine>): string =>
  JSON.stringify({
    idMachine: m.idMachine,
    idNumber: m.idNumber,
    description: m.description,
    serviceType: m.serviceType,
    problemDescription: m.problemDescription ?? null,
    workDescription: m.workDescription ?? null,
    futureWorksDescription: m.futureWorksDescription ?? null,
    customerReference: m.customerReference ?? null,
    softwareVersion: m.softwareVersion ?? null,
    osVersion: m.osVersion ?? null,
    followUpAfterService: m.followUpAfterService ?? null,
  });

const MachineForm: React.FC = () => {
  const { reportId, machineId } = useParams<{ reportId: string; machineId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSuccess, showError, NotificationContainer } = useNotification();
  const { confirm, ConfirmDialog } = useConfirm();
  const isEditMode = machineId !== 'new';

  const { report, loading: reportLoading, error: reportError } = useReport(reportId);
  const { machine: loadedMachine, loading: machineLoading, refresh: refreshMachine } = useMachine(isEditMode && machineId ? machineId : undefined);
  const isReadonlyForForeignAgent = useForeignAgentReadonly(report?.customerId);

  const isAiEnabled =
    process.env.REACT_APP_AI_ASSISTANT_ENABLED !== 'false' &&
    authService.getCurrentUser()?.email === 'admin@darren.local';

  const [machine, setMachine] = useState<Partial<ReportMachine>>({
    idMachine: '',
    idNumber: '',
    description: '',
    serviceType: undefined,
    problemDescription: '',
    workDescription: '',
    futureWorksDescription: '',
    customerReference: '',
    softwareVersion: '',
    osVersion: '',
    followUpAfterService: '',
    spareParts: [],
    timeLogs: []
  });

  const [newTimeLog, setNewTimeLog] = useState<ReportTimeLogInput>({
    logDate: new Date().toISOString(),
    startTime: new Date().toISOString(),
    finishTime: new Date().toISOString(),
    breakTime: 0,
    travelTime: 0,
    travelKm: 0
  });
  const [breakTimeInput, setBreakTimeInput] = useState('00:00');
  const [travelTimeInput, setTravelTimeInput] = useState('00:00');

  const [showMachineModal, setShowMachineModal] = useState(false);
  const [showAiAssistant, setShowAiAssistant] = useState(false);
  const lastSavedRef = useRef<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [timeLogsSortDirection, setTimeLogsSortDirection] = useState<'asc' | 'desc'>('desc');

  const loading = React.useMemo(
    () => reportLoading || (isEditMode && machineLoading),
    [reportLoading, isEditMode, machineLoading]
  );

  const sortedTimeLogs = React.useMemo(() => {
    if (!machine.timeLogs || machine.timeLogs.length === 0) return [];
    return [...machine.timeLogs].sort((a, b) => {
      const dateA = new Date(a.logDate).getTime();
      const dateB = new Date(b.logDate).getTime();
      return timeLogsSortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }, [machine.timeLogs, timeLogsSortDirection]);

  const toggleTimeLogsSortDirection = useCallback(() => {
    setTimeLogsSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  }, []);

  const handleProblemDescriptionChange = useCallback((problemDescription: string) => {
    setMachine(prev => ({ ...prev, problemDescription }));
  }, []);

  const handleServiceTypeChange = useCallback((serviceType: ServiceType) => {
    setMachine(prev => {
      const updates: Partial<ReportMachine> = { serviceType };
      if (serviceType === ServiceType.TYPE1 && !prev.workDescription?.trim()) {
        updates.workDescription = t('machineForm.installationDocumentationSentence');
      }
      return { ...prev, ...updates };
    });
  }, [t]);

  const handleMachineSelect = useCallback((selectedMachine: Machine) => {
    setMachine(prev => ({
      ...prev,
      idMachine: selectedMachine.id,
      idNumber: selectedMachine.serialNumber || '',
      description: selectedMachine.displayDescription || selectedMachine.descriptionPublic || ''
    }));
    setShowMachineModal(false);
  }, []);

  const closeMachineModal = useCallback(() => {
    setShowMachineModal(false);
  }, []);

  useEffect(() => {
    if (reportError || (!reportLoading && !report)) {
      showError(t('messages.reportNotFound'));
      navigate('/');
    }
  }, [report, reportLoading, reportError, showError, t, navigate]);

  useEffect(() => {
    if (loadedMachine) {
      setMachine(loadedMachine);
      lastSavedRef.current = serializeMachineForDirtyCheck(loadedMachine);
    }
  }, [loadedMachine]);

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!machine.idMachine) {
      showError(t('messages.fillMachineNumber'));
      return false;
    }

    if (isEditMode && machineId && lastSavedRef.current !== null) {
      if (serializeMachineForDirtyCheck(machine) === lastSavedRef.current) {
        return true;
      }
    }

    setIsSaving(true);
    try {
      if (isEditMode && machineId) {
        await apiClient.updateMachine(machineId, {
          reportId: reportId!,
          idMachine: machine.idMachine,
          idNumber: machine.idNumber || machine.idMachine,
          description: machine.description || '',
          serviceType: machine.serviceType || ServiceType.TYPE1,
          problemDescription: machine.problemDescription,
          workDescription: machine.workDescription,
          futureWorksDescription: machine.futureWorksDescription,
          customerReference: machine.customerReference,
          softwareVersion: machine.softwareVersion,
          osVersion: machine.osVersion,
          followUpAfterService: machine.followUpAfterService,
          manuallyAdded: machine.manuallyAdded || false
        });
        showSuccess(t('messages.machineUpdatedSuccess'));
        lastSavedRef.current = serializeMachineForDirtyCheck(machine);
      } else {
        const newMachine: ReportMachineRequest = {
          reportId: reportId!,
          idMachine: machine.idMachine,
          idNumber: machine.idNumber || machine.idMachine,
          description: machine.description || '',
          serviceType: machine.serviceType || ServiceType.TYPE1,
          problemDescription: machine.problemDescription,
          workDescription: machine.workDescription,
          futureWorksDescription: machine.futureWorksDescription,
          customerReference: machine.customerReference,
          softwareVersion: machine.softwareVersion,
          osVersion: machine.osVersion,
          followUpAfterService: machine.followUpAfterService,
          manuallyAdded: true
        };
        const createdMachine = await apiClient.addMachineToReport(newMachine);
        showSuccess(t('messages.machineAddedSuccess'));
        setMachine(prev => ({ ...prev, ...createdMachine }));
        lastSavedRef.current = serializeMachineForDirtyCheck(createdMachine);
        navigate(`/report/${reportId}/machine/${createdMachine.id}`, { replace: true });
      }
      return true;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showError(`${t('messages.errorSavingMachine')}: ${message}`);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [machine, isEditMode, machineId, reportId, showSuccess, showError, t, navigate]);

  const handleAddTimeLog = useCallback(async () => {
    if (!isEditMode || !machine.id) return;

    const saved = await handleSave();
    if (!saved) return;

    const startTime = new Date(newTimeLog.startTime!);
    const finishTime = new Date(newTimeLog.finishTime!);

    if (finishTime <= startTime) {
      showError(t('messages.finishTimeAfterStart'));
      return;
    }

    const deltaMinutes = (finishTime.getTime() - startTime.getTime()) / 60000;
    const breakMin = timeStringToMinutes(breakTimeInput);
    const travelMin = timeStringToMinutes(travelTimeInput);

    if (deltaMinutes < breakMin + travelMin) {
      showError(t('messages.totalTimeGreaterThanBreakTravel'));
      return;
    }

    try {
      const timeLogRequest: ReportTimeLogRequest = {
        reportMachineId: machine.id,
        logDate: newTimeLog.logDate!,
        startTime: newTimeLog.startTime!,
        finishTime: newTimeLog.finishTime!,
        breakTime: breakMin,
        travelTime: travelMin,
        travelKm: newTimeLog.travelKm
      };
      await apiClient.addTimeLog(timeLogRequest);
      // Reload machine data
      await refreshMachine();
      // Reset form
      setNewTimeLog({
        logDate: new Date().toISOString(),
        startTime: new Date().toISOString(),
        finishTime: new Date().toISOString(),
        breakTime: 0,
        travelTime: 0,
        travelKm: 0
      });
      setBreakTimeInput('00:00');
      setTravelTimeInput('00:00');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showError(`${t('messages.errorAddingTimeLog')}: ${message}`);
    }
  }, [isEditMode, machine.id, newTimeLog, breakTimeInput, travelTimeInput, showError, t, refreshMachine, handleSave]);

  const handleDeleteTimeLog = useCallback(async (timeLogId: string) => {
    const confirmed = await confirm({ message: t('confirmMessages.deleteTimeLog') });
    if (!confirmed) return;

    try {
      await apiClient.deleteTimeLog(timeLogId);
      // Reload machine data
      await refreshMachine();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showError(`${t('messages.errorDeletingTimeLog')}: ${message}`);
    }
  }, [confirm, t, refreshMachine, showError]);

  const handleBeforeNavigate = useCallback(async (_path: string): Promise<boolean> => {
    if (!isEditMode && !machine.idMachine) {
      return true;
    }
    const canSave = !!report && !isReadonlyForForeignAgent &&
      report.status !== ReportStatus.SIGNED &&
      report.status !== ReportStatus.CLOSED;
    if (canSave) {
      return handleSave();
    }
    return true;
  }, [report, isReadonlyForForeignAgent, handleSave, isEditMode, machine.idMachine]);

  const handleSparePartsClick = useCallback(async () => {
    const saved = await handleSave();
    if (!saved) return;
    navigate(`/report/${reportId}/machine/${machineId}/spareparts`);
  }, [handleSave, navigate, reportId, machineId]);
  /**
   * Machine Info & Service Preparation readonly
   * According to PDF specs: E in NEW/READY/IN_PROGRESS, R in SIGNED/CLOSED
   * Exception: When adding a new machine (!isEditMode), fields are always editable
   */
  const isFieldReadonly = React.useMemo((): boolean => {
    if (!report) return false;
    // When adding a new machine, fields should be editable to allow machine selection
    if (!isEditMode) return false;
    if (isReadonlyForForeignAgent) return true;
    return report.status === ReportStatus.IN_PROGRESS ||
      report.status === ReportStatus.SIGNED ||
      report.status === ReportStatus.CLOSED;
  }, [report, isEditMode, isReadonlyForForeignAgent]);

  /**
   * Save button disabled: Only in SIGNED/CLOSED states
   * According to PDF specs: Save is available in NEW/READY/IN_PROGRESS
   */
  const isSaveDisabled = React.useMemo((): boolean => {
    if (!report) return false;
    if (isReadonlyForForeignAgent) return true;
    return report.status === ReportStatus.SIGNED ||
      report.status === ReportStatus.CLOSED;
  }, [report, isReadonlyForForeignAgent]);

  /**
   * Service Preparation section visibility
   * According to PDF specs: Visible in ADD MACHINE and all existing machine statuses except CANCELED
   */
  const isServicePreparationVisible = React.useMemo((): boolean => {
    if (!report) return false;
    // Hidden in CANCELED status
    if (report.status === ReportStatus.CANCELED) return false;
    // Visible for ADD MACHINE and existing machines
    return true;
  }, [report]);

  /**
   * Service Preparation readonly (serviceType field)
   * According to PDF specs: E in NEW/READY/IN_PROGRESS, R in SIGNED/CLOSED
   * Same rule as Issue Notified (isProblemDescriptionReadonly): always read-only for foreign agents
   * Exception: When adding a new machine (!isEditMode), fields are always editable
   */
  const isServicePreparationReadonly = React.useMemo((): boolean => {
    if (!report) return false;
    // When adding a new machine, fields should be editable
    if (!isEditMode) return false;
    if (isReadonlyForForeignAgent) return true;
    return report.status === ReportStatus.SIGNED || report.status === ReportStatus.CLOSED;
  }, [report, isEditMode, isReadonlyForForeignAgent]);

  /**
   * Problem description (Issue Notified) readonly
   * Editable in NEW/READY/IN_PROGRESS, read-only in SIGNED/CLOSED
   */
  const isProblemDescriptionReadonly = React.useMemo((): boolean => {
    if (!report) return false;
    if (!isEditMode) return false;
    if (isReadonlyForForeignAgent) return true;
    return report.status === ReportStatus.SIGNED || report.status === ReportStatus.CLOSED;
  }, [report, isEditMode, isReadonlyForForeignAgent]);

  /**
   * Notes section visibility (MachineInfoSection)
   * According to PDF specs: H in NEW/READY, E in IN_PROGRESS, R in SIGNED/CLOSED
   */
  const isNotesSectionVisible = React.useMemo((): boolean => {
    if (!report) return false;
    return isEditMode && (
      report.status === ReportStatus.IN_PROGRESS ||
      report.status === ReportStatus.SIGNED ||
      report.status === ReportStatus.CLOSED
    );
  }, [report, isEditMode]);

  /**
   * Notes section editability: Editable only in IN_PROGRESS, read-only in SIGNED/CLOSED
   */
  const isNotesReadonly = React.useMemo((): boolean => {
    if (!report) return false;
    return report.status === ReportStatus.SIGNED || report.status === ReportStatus.CLOSED;
  }, [report]);

  /**
   * Time Logs section visibility: Hidden in NEW/READY, visible in IN_PROGRESS/SIGNED/CLOSED
   */
  const isTimeLogSectionVisible = React.useMemo((): boolean => {
    if (!report) return false;
    return isEditMode && (
      report.status === ReportStatus.IN_PROGRESS ||
      report.status === ReportStatus.SIGNED ||
      report.status === ReportStatus.CLOSED
    );
  }, [report, isEditMode]);

  /**
   * Time Logs editability: Editable in IN_PROGRESS, read-only in SIGNED/CLOSED
   */
  const isTimeLogReadonly = React.useMemo((): boolean => {
    if (!report) return false;
    if (isReadonlyForForeignAgent) return true;
    return report.status === ReportStatus.SIGNED || report.status === ReportStatus.CLOSED;
  }, [report, isReadonlyForForeignAgent]);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: '', translateKey: 'breadcrumb.dashboard', path: getDashboardPath(authService.getCurrentUser()), onClick: handleBeforeNavigate },
    { label: `${t('breadcrumb.report')} #${report?.number || ''}`, path: `/report/${reportId}`, onClick: handleBeforeNavigate },
    {
      label: isEditMode
        ? `${t('breadcrumb.machine')}: ${machine.idNumber || ''}`
        : t('breadcrumb.newMachine')
    }
  ];

  if (loading) {
    return <div className="loading">{t('app.loading')}</div>;
  }

  return (
    <div className="machine-form">
      <NotificationContainer />
      <ConfirmDialog />
      <FormHeader breadcrumbItems={breadcrumbItems}>
        <div className="header-actions">
          <button onClick={handleSave} className={`btn-save ${isSaving ? 'btn-loading' : ''}`} disabled={isSaveDisabled || isSaving}>
            {isSaving ? (
              <><span className="btn-spinner"></span> {t('common.saving')}</>
            ) : (
              <><span className="material-icons">save</span> {isEditMode ? t('common.update') : t('common.save')}</>
            )}
          </button>
        </div>
      </FormHeader>

      <div className="form-layout">
        {/* Main Content */}
        <div className="form-main-content">
          {/* Machine Selection */}
          <section className="form-section">
            <h3>{t('machineForm.machineSelection')}</h3>
            <div className="form-grid">
              <div className="form-field">
                <label>{t('machineForm.machineNumber')} *</label>
                <div className="input-with-button">
                  <input
                    type="text"
                    value={machine.idNumber || ''}
                    readOnly
                    className="input-readonly"
                    required
                  />
                  {!isEditMode && !isFieldReadonly && (
                    <button
                      type="button"
                      onClick={() => setShowMachineModal(true)}
                      className="btn-select"
                    >
                      <span className="material-icons">search</span>
                    </button>
                  )}
                </div>
              </div>
              <div className="form-field">
                <label>{t('machineForm.description')}</label>
                <input
                  type="text"
                  value={machine.description || ''}
                  onChange={(e) => setMachine({ ...machine, description: e.target.value })}
                  disabled={isEditMode || isFieldReadonly}
                />
              </div>
            </div>
          </section>

          {/* Service Preparation - Visible in ADD MACHINE and all states, Editable in NEW/READY/IN_PROGRESS, Read-only in SIGNED/CLOSED */}
          {isServicePreparationVisible && (
            <ServicePreparationSection
              serviceType={machine.serviceType as ServiceType | undefined}
              problemDescription={machine.problemDescription || ''}
              onServiceTypeChange={handleServiceTypeChange}
              onProblemDescriptionChange={handleProblemDescriptionChange}
              readOnly={isServicePreparationReadonly}
              problemDescriptionReadOnly={isProblemDescriptionReadonly}
              showSparePartsButton={isEditMode}
              onSparePartsClick={handleSparePartsClick}
            />
          )}

          {/* Machine Info (Notes) - Hidden in NEW/READY, Editable in IN_PROGRESS, Read-only in SIGNED/CLOSED */}
          {isNotesSectionVisible && (
            <MachineInfoSection
              customerReference={machine.customerReference || ''}
              softwareVersion={machine.softwareVersion || ''}
              osVersion={machine.osVersion || ''}
              workDescription={machine.workDescription || ''}
              futureWorksDescription={machine.futureWorksDescription || ''}
              followUpAfterService={machine.followUpAfterService || ''}
              onCustomerReferenceChange={(customerReference) => setMachine({ ...machine, customerReference })}
              onSoftwareVersionChange={(softwareVersion) => setMachine({ ...machine, softwareVersion })}
              onOsVersionChange={(osVersion) => setMachine({ ...machine, osVersion })}
              onWorkDescriptionChange={(workDescription) => setMachine({ ...machine, workDescription })}
              onFutureWorksDescriptionChange={(futureWorksDescription) => setMachine({ ...machine, futureWorksDescription })}
              onFollowUpAfterServiceChange={(followUpAfterService) => setMachine({ ...machine, followUpAfterService })}
              readOnly={isNotesReadonly}
            />
          )}

          {/* Time Logs - Hidden in NEW/READY, Editable in IN_PROGRESS/SIGNED, Read-only in CLOSED */}
          {isTimeLogSectionVisible && (
            <section className="form-section">
              <h3>{t('machineForm.timeLogs')}</h3>

              {/* Add Time Log Form - Hidden in CLOSED */}
              {!isTimeLogReadonly && (
                <div className="time-log-form">
                  <div className="form-grid">
                    <div className="form-field">
                      <label>{t('machineForm.date')}</label>
                      <input
                        type="date"
                        value={format(new Date(newTimeLog.logDate!), 'yyyy-MM-dd')}
                        onChange={(e) => setNewTimeLog({ ...newTimeLog, logDate: new Date(e.target.value).toISOString() })}
                      />
                    </div>
                    <div className="form-field">
                      <label>{t('machineForm.startTime')}</label>
                      <input
                        type="time"
                        value={format(new Date(newTimeLog.startTime!), 'HH:mm')}
                        onChange={(e) => {
                          const [hours, minutes] = e.target.value.split(':');
                          const date = new Date(newTimeLog.startTime!);
                          date.setHours(parseInt(hours), parseInt(minutes));
                          setNewTimeLog({ ...newTimeLog, startTime: date.toISOString() });
                        }}
                      />
                    </div>
                    <div className="form-field">
                      <label>{t('machineForm.finishTime')}</label>
                      <input
                        type="time"
                        value={format(new Date(newTimeLog.finishTime!), 'HH:mm')}
                        onChange={(e) => {
                          const [hours, minutes] = e.target.value.split(':');
                          const date = new Date(newTimeLog.finishTime!);
                          date.setHours(parseInt(hours), parseInt(minutes));
                          setNewTimeLog({ ...newTimeLog, finishTime: date.toISOString() });
                        }}
                      />
                    </div>
                    <div className="form-field">
                      <label>{t('machineForm.breakTime')}</label>
                      <input
                        type="text"
                        placeholder="hh:mm"
                        pattern="[0-9][0-9]:[0-5][0-9]"
                        value={breakTimeInput}
                        onChange={(e) => setBreakTimeInput(e.target.value)}
                      />
                    </div>
                    <div className="form-field">
                      <label>
                        {t('machineForm.travelTime')}
                        <InfoTooltip text={t('machineForm.travelTimeTooltip')} />
                      </label>
                      <input
                        type="text"
                        placeholder="hh:mm"
                        pattern="[0-9][0-9]:[0-5][0-9]"
                        value={travelTimeInput}
                        onChange={(e) => setTravelTimeInput(e.target.value)}
                      />
                    </div>
                    <div className="form-field">
                      <label>{t('machineForm.travelKm')}</label>
                      <input
                        type="number"
                        min="0"
                        value={newTimeLog.travelKm || 0}
                        onChange={(e) => setNewTimeLog({ ...newTimeLog, travelKm: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  <button onClick={handleAddTimeLog} className="btn-add-timelog">
                    <span className="material-icons">add</span> {t('machineForm.addTimeLog')}
                  </button>
                </div>
              )}

              {/* Time Logs List */}
              <div className="time-logs-list">
                {machine.timeLogs && machine.timeLogs.length > 0 ? (
                  <table className="time-logs-table">
                    <thead>
                      <tr>
                        <th className="sortable" onClick={toggleTimeLogsSortDirection}>
                          {t('machineForm.date')}
                          <span className={`sort-icon sort-icon-${timeLogsSortDirection}`}>
                            {timeLogsSortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        </th>
                        <th>{t('machineForm.start')}</th>
                        <th>{t('machineForm.finish')}</th>
                        <th>{t('machineForm.workedMin')}</th>
                        <th>{t('machineForm.breakMin')}</th>
                        <th>
                          {t('machineForm.travelMin')}
                          <InfoTooltip text={t('machineForm.travelTimeTooltip')} />
                        </th>
                        <th>{t('machineForm.travelKm')}</th>
                        {!isTimeLogReadonly && <th>{t('common.actions')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTimeLogs.map((log) => (
                        <tr key={log.id}>
                          <td>{safeFormatDate(log.logDate, 'dd/MM/yyyy')}</td>
                          <td>{safeFormatDate(log.startTime, 'HH:mm')}</td>
                          <td>{safeFormatDate(log.finishTime, 'HH:mm')}</td>
                          <td>{minutesToTimeString(Math.max(0, differenceInMinutes(new Date(log.finishTime), new Date(log.startTime)) - (log.breakTime || 0) - (log.travelTime || 0)))}</td>
                          <td>{minutesToTimeString(log.breakTime || 0)}</td>
                          <td>{minutesToTimeString(log.travelTime || 0)}</td>
                          <td>{log.travelKm}</td>
                          {!isTimeLogReadonly && (
                            <td>
                              <button onClick={() => handleDeleteTimeLog(log.id)} className="btn-delete">
                                <span className="material-icons">delete</span>
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="no-data">{t('machineForm.noTimeLogs')}</p>
                )}
              </div>
            </section>
          )}
        </div>

        {/* Sidebar - Right Column */}
        <aside className="form-sidebar">
          <ReportStatusInfo report={report} />
          <ReportInfo report={report} />
        </aside>
      </div>

      {showMachineModal && (
        <MachineSelectionModal
          onSelect={handleMachineSelect}
          onClose={closeMachineModal}
        />
      )}

      {/* AI Assistant floating button — visible when a machine is selected and feature is enabled */}
      {isAiEnabled && machine.idMachine && (
        <button
          className="ai-assistant-fab"
          onClick={() => setShowAiAssistant(true)}
          title="Apri assistente AI"
          aria-label="Apri assistente AI"
        >
          <span className="material-icons">auto_awesome</span>
        </button>
      )}

      {isAiEnabled && showAiAssistant && machine.idMachine && (
        <AiAssistantModal
          machineId={machine.idMachine}
          machineDescription={machine.description}
          problemDescription={machine.problemDescription ?? undefined}
          onClose={() => setShowAiAssistant(false)}
        />
      )}
    </div>
  );
};

export default MachineForm;
